import { useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { assert } from 'assert-ts';
import { useMemo } from 'react';
import { maxRoutesQueryOptions } from '#api/routes.queries.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import { Table } from '#components/patterns/table/Table.tsx';
import type { RouteListItem } from '#dn-types/route.ts';
import styles from './RoutesList.module.css';

type Props = {
  items: Array<{ routeId: string }>;
  onRemove: (id: number) => void;
  children?: React.ReactNode;
};

const columnHelper = createColumnHelper<{ route: RouteListItem }>();

export function RoutesList({ onRemove, items, children }: Props) {
  const routes = useSuspenseQuery({
    ...maxRoutesQueryOptions,
    select({ data }) {
      return {
        data: items.map(({ routeId }) => ({
          route: assert(
            data.find((route) => route.id === routeId),
            'Expected to find a route matching routeId'
          ),
        })),
      };
    },
  }).data.data;

  const columns = useMemo(
    () => [
      columnHelper.accessor('route.name', {
        header: 'Name',
      }),
      columnHelper.accessor('route.description', {
        header: 'Description',
      }),
      columnHelper.accessor('route.routableCIDRs', {
        header: 'CIDRs',
        cell(info) {
          const cidrs = Object.keys(info.getValue());

          return cidrs.join(', ');
        },
      }),
      columnHelper.display({
        id: 'route-action-menu',
        header: '',
        size: 34,
        cell(info) {
          return (
            <div className="flex justify-end">
              <MeatballMenu>
                <MenuItem
                  onClick={() => {
                    const index = items.findIndex((i) => i.routeId === info.row.original.route.id);
                    onRemove(index);
                  }}
                >
                  Remove
                </MenuItem>
              </MeatballMenu>
            </div>
          );
        },
      }),
    ],
    [items, onRemove]
  );

  return (
    <div>
      {items.length ?
        <div className="mb-16">
          <Table className={styles.RoutesList} data={routes} columns={columns} hideOverflowX />
        </div>
      : <Banner className="mb-16">No routes defined</Banner>}
      {children}
    </div>
  );
}
